<script setup lang="ts">
import { Circle as CircleIcon, Mountain as MountainIcon } from '@lucide/vue'
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'

import AuthModal from '@/components/auth/AccountModal.vue'
import UserMenu from '@/components/auth/UserMenu.vue'
import SyncStatusDialog from '@/components/dialogs/SyncStatusDialog.vue'
import ShareModal from '@/components/map/modals/ShareWorkspaceModal.vue'
import { useEventListener } from '@/composables/useEventListener'
import { useSyncStatusIndicator } from '@/composables/useSyncStatusIndicator'
import { useSafeAreaHeaderOffset, useSafeAreaInsets } from '@/services/safeAreaService'
import { resolveSitePagePath } from '@/services/sitePageRouteService'
import { useAuthStore } from '@/stores/authStore'
import { useRecordingStore } from '@/stores/recordingStore'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { Z_INDEX } from '@/utils/zIndex'

const uiStore = useUiStore()
const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()
const recordingStore = useRecordingStore()
const router = useRouter()
const route = useRoute()

// Sync status dialog state
const isSyncDialogOpen = ref(false)
const safeAreaInsets = useSafeAreaInsets()
const safeAreaHeaderOffset = useSafeAreaHeaderOffset(48)

const {
  iconComponent: syncIconComponent,
  iconStateClass: syncIconStateClass,
  title: syncButtonTitle,
  badgeCount: syncBadgeCount,
  badgeToneClass: syncBadgeToneClass,
  isSignedOutWithQueue: syncSignedOutWithQueue,
} = useSyncStatusIndicator()
const syncIconClass = computed(() => `w-[18px] h-[18px] ${syncIconStateClass.value}`)

const showRecordingIndicator = computed(
  () => recordingStore.hasRecording || recordingStore.hasStoppedDraft,
)

// Share modal state
const isShareModalOpen = ref(false)

// Auth modal state
const isAuthModalOpen = ref(false)
const authModalView = ref<'login' | 'signup' | 'reset' | 'reset-confirm' | 'profile-edit'>('login')
const resetToken = ref<string>()
const signupInviteCode = ref<string>()

const closeShareModal = () => {
  isShareModalOpen.value = false
}

// Auth modal functions
const openAuthModal = () => {
  authModalView.value = 'login'
  signupInviteCode.value = undefined
  isAuthModalOpen.value = true
}

const openSignupModal = () => {
  authModalView.value = 'signup'
  signupInviteCode.value = undefined
  isAuthModalOpen.value = true
}

const openProfileEditModal = () => {
  authModalView.value = 'profile-edit'
  isAuthModalOpen.value = true
}

const closeAuthModal = () => {
  isAuthModalOpen.value = false
}

const firstRouteValue = (value: unknown) => (Array.isArray(value) ? value[0] : value)

const handleAuthSuccess = async () => {
  closeAuthModal()
  uiStore.hideLandingPage()
  if (route.name === 'account-invitations') {
    return
  }
  // Stay on the current page only when logged in on a public collection.
  // A private workspace URL while unauthenticated means the user couldn't see any
  // private data yet, so redirect to the overview in that case.
  const onPublicWorkspace = !!route.params.workspaceId && workspaceStore.isPublic
  const onPersonalSurface =
    route.name === 'pinned' || route.name === 'library' || route.name === 'workspaces'
  if (!onPublicWorkspace && !onPersonalSurface) {
    await router.push({ name: 'pinned' })
  }
}

const isStaticPageRoute = (path: string) =>
  path === '/about' ||
  path === '/home' ||
  path === '/imprint' ||
  path === '/privacy-policy' ||
  path === '/data-protection' ||
  path === '/terms-of-use' ||
  path === '/keyboard-shortcuts'

const openStaticPage = async (pageId: string) => {
  const targetPath = resolveSitePagePath(pageId)
  if (route.path === targetPath) {
    return
  }

  await router.push(targetPath)
}

const handleLogoClick = () => {
  uiStore.expandMobileBottomPanel()
  void router.push({ name: 'pinned' })
}

// Check for auth query parameter on mount and route changes
const checkAuthParam = () => {
  const authParam = route.query.auth
  const routeInviteCode = firstRouteValue(route.params.inviteCode)
  if (route.name === 'account-invite-signup' && typeof routeInviteCode === 'string') {
    authModalView.value = 'signup'
    signupInviteCode.value = routeInviteCode
    isAuthModalOpen.value = true
    return
  }

  const inviteCode = firstRouteValue(route.query.inviteCode)
  if (authParam === 'login') {
    authModalView.value = 'login'
    signupInviteCode.value = undefined
    isAuthModalOpen.value = true
    // Clean up the URL by removing the auth parameter
    router.replace({
      path: route.path,
      query: { ...route.query, auth: undefined },
    })
  } else if (authParam === 'signup') {
    authModalView.value = 'signup'
    signupInviteCode.value = typeof inviteCode === 'string' ? inviteCode : undefined
    isAuthModalOpen.value = true
    // Clean up the URL by removing auth-only signup parameters after reading them.
    router.replace({
      path: route.path,
      query: { ...route.query, auth: undefined, inviteCode: undefined },
    })
  } else if (authParam === 'reset-confirm') {
    authModalView.value = 'reset-confirm'
    signupInviteCode.value = undefined
    resetToken.value = route.query.token as string
    isAuthModalOpen.value = true
    // Clean up the URL by removing the auth and token parameters
    router.replace({
      path: route.path,
      query: { ...route.query, auth: undefined, token: undefined },
    })
  }
}

// Watch for route changes to check auth parameter
watch(
  () => [
    route.name,
    route.params.inviteCode,
    route.query.auth,
    route.query.token,
    route.query.inviteCode,
  ],
  () => {
    checkAuthParam()
  },
)

// Close landing page overlay on any route navigation (header links, etc.)
watch(
  () => route.fullPath,
  (newPath) => {
    if (uiStore.isLandingPageVisible && !isStaticPageRoute(newPath)) {
      uiStore.hideLandingPage()
    }
  },
)

// Check auth parameter on mount
onMounted(async () => {
  checkAuthParam()
})

// Listen for open-auth-modal events from other components (e.g. landing page, anonymous dialog)
useEventListener(window, 'open-auth-modal', ((event: CustomEvent) => {
  const view = event.detail?.view
  if (view === 'signup') {
    authModalView.value = 'signup'
    signupInviteCode.value = event.detail?.inviteCode
  } else if (view === 'profile-edit') {
    authModalView.value = 'profile-edit'
    signupInviteCode.value = undefined
  } else {
    authModalView.value = 'login'
  }
  isAuthModalOpen.value = true
}) as EventListener)

useEventListener(window, 'open-sync-status', () => {
  isSyncDialogOpen.value = true
})

useEventListener(window, 'open-static-site', ((event: CustomEvent) => {
  openStaticPage(event.detail)
}) as EventListener)

// Watch dialog states to update UI store
watch(
  [
    isAuthModalOpen,
    isShareModalOpen,
    isSyncDialogOpen,
    () => recordingStore.isModalOpen,
    () => uiStore.isLandingPageVisible,
  ],
  ([authOpen, shareOpen, syncOpen, recordingOpen, landingOpen]) => {
    const anyDialogOpen = authOpen || shareOpen || syncOpen || recordingOpen || landingOpen
    uiStore.setDialogOpen(anyDialogOpen)
  },
  { immediate: true },
)

const headerStyle = computed(() => {
  const style: Record<string, string> = {
    zIndex: Z_INDEX.HEADER.toString(),
    top: '0px',
    paddingTop: safeAreaHeaderOffset.value.paddingTop,
    minHeight: safeAreaHeaderOffset.value.minHeight,
    paddingLeft: safeAreaInsets.value.left,
    paddingRight: safeAreaInsets.value.right,
  }

  return style
})
</script>

<template>
  <header
    :class="[
      'absolute top-0 left-0 right-0',
      'bg-[#FEFEFE]',
      'h-12',
      'hidden sm:flex items-center',
    ]"
    :style="headerStyle"
  >
    <!-- Logo: left on mobile, aligned with desktop toolbar inset on sm+ -->
    <button
      class="sm:absolute sm:left-7 flex items-center gap-2 cursor-pointer pl-1 sm:pl-0"
      aria-label="Routecamp home"
      @click="handleLogoClick"
    >
      <div class="flex items-center justify-center w-7 h-7 bg-emerald-700 rounded-md">
        <MountainIcon class="w-4 h-4 text-white" />
      </div>
      <span class="text-base font-semibold text-gray-900 whitespace-nowrap"> routecamp </span>
    </button>

    <!-- Right: Actions -->
    <div class="flex items-center gap-1.5 ml-auto pr-6 sm:pr-8">
      <!-- Group 1: Sync/Recording -->
      <div class="flex items-center gap-1.5">
        <button
          v-if="authStore.isAuthenticated && showRecordingIndicator"
          class="flex items-center justify-center w-7 h-7 rounded hover:cursor-pointer hover:bg-gray-100"
          aria-label="Open recording"
          title="Recording"
          @click="recordingStore.openModal"
        >
          <CircleIcon
            class="h-4.5 w-4.5 text-red-600 fill-current"
            :class="{ 'animate-pulse': recordingStore.isActive }"
          />
        </button>
        <button
          v-else-if="authStore.isAuthenticated || syncSignedOutWithQueue"
          class="relative flex items-center justify-center w-7 h-7 rounded hover:cursor-pointer hover:bg-gray-100"
          aria-label="Open sync status"
          :title="syncButtonTitle"
          data-testid="header-sync-status-button"
          @click="isSyncDialogOpen = true"
        >
          <component :is="syncIconComponent" :class="syncIconClass" />
          <!-- Queued-item count badge (sync-feedback.md §4.4): shape + number, never colour alone -->
          <span
            v-if="syncBadgeCount > 0"
            :class="[
              'absolute -top-1 -right-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full px-0.5 text-[9px] font-semibold leading-none',
              syncBadgeToneClass,
            ]"
            data-testid="header-sync-badge"
          >
            {{ syncBadgeCount > 99 ? '99+' : syncBadgeCount }}
          </span>
        </button>
      </div>

      <!-- Divider before user menu -->
      <div class="w-px h-4 bg-gray-200 mx-1" />

      <!-- Group 2: User -->
      <UserMenu
        @open-auth-modal="openAuthModal"
        @open-profile-edit="openProfileEditModal"
        @open-sync-dialog="isSyncDialogOpen = true"
        @open-static-site="openStaticPage"
      />

      <button
        v-if="!authStore.isAuthenticated"
        class="inline-flex items-center px-4 py-1.5 text-sm text-white bg-emerald-700 rounded-lg shadow-sm hover:bg-emerald-600 transition-colors cursor-pointer"
        aria-label="Sign up"
        @click="openSignupModal"
      >
        Sign up
      </button>
    </div>
  </header>

  <!-- Share Modal -->
  <ShareModal :is-open="isShareModalOpen" @close="closeShareModal" />

  <!-- Auth Modal -->
  <AuthModal
    :is-open="isAuthModalOpen"
    :initial-view="authModalView"
    :reset-token="resetToken"
    :initial-invite-code="signupInviteCode"
    @close="closeAuthModal"
    @auth-success="handleAuthSuccess"
  />

  <!-- Sync Status Dialog -->
  <SyncStatusDialog :is-open="isSyncDialogOpen" @close="isSyncDialogOpen = false" />
</template>
